import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSuggestPortageContact } from '@/hooks/queries/usePortageCompanies'
import {
  normalizePortageContactNames,
  type PortageContact,
} from '@/services/portageService'

interface PortageContactSuggestDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  portageCompanyId: string | null
  /** Nom de la société de portage / intermédiaire (affiché dans la description). */
  portageCompanyName?: string | null
  onSuggested: (contact: PortageContact) => void
  /**
   * `freelancer` : wording + callout email en fin d'inscription.
   * `team` : titre « Ajouter un contact », sans description (création mission).
   */
  variant?: 'freelancer' | 'team'
}

/**
 * Dialogue de suggestion d'un responsable de compte absent de la liste verified.
 */
export function PortageContactSuggestDialog({
  open,
  onOpenChange,
  portageCompanyId,
  portageCompanyName,
  onSuggested,
  variant = 'freelancer',
}: PortageContactSuggestDialogProps) {
  const { t } = useTranslation()
  const isTeamVariant = variant === 'team'
  const companyLabel =
    typeof portageCompanyName === 'string' && portageCompanyName.trim()
      ? portageCompanyName.trim()
      : t('freelancerOnboarding.portageCompany.selectLabel')
  const suggestDescription = t('freelancerOnboarding.portageContact.suggestDescription', {
    company: companyLabel,
  })
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const suggestMutation = useSuggestPortageContact(portageCompanyId)
  const isSubmitting = suggestMutation.isPending

  const reset = () => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setErrors({})
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const validate = () => {
    const next: Record<string, string> = {}
    const normalized = normalizePortageContactNames(firstName, lastName)
    if (!normalized.firstName) {
      next.firstName = t('freelancerOnboarding.portageContact.firstNameRequired')
    }
    if (!normalized.lastName) {
      next.lastName = t('freelancerOnboarding.portageContact.lastNameRequired')
    }
    if (!email.trim()) {
      next.email = t('freelancerOnboarding.portageContact.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = t('freelancerOnboarding.portageContact.emailInvalid')
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!portageCompanyId || !validate()) return
    try {
      const normalized = normalizePortageContactNames(firstName, lastName)
      const contact = await suggestMutation.mutateAsync({
        first_name: normalized.firstName ?? undefined,
        last_name: normalized.lastName ?? undefined,
        email: email.trim(),
      })
      onSuggested(contact)
      reset()
      onOpenChange(false)
    } catch {
      // Errors handled by React Query
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="z-21474830003" overlayClassName="z-21474830002">
        <DialogHeader>
          <DialogTitle>
            {isTeamVariant
              ? t('freelancerOnboarding.portageContact.addContact')
              : t('freelancerOnboarding.portageContact.suggestTitle')}
          </DialogTitle>
          {isTeamVariant ? (
            <DialogDescription className="sr-only">
              {t('freelancerOnboarding.portageContact.addContact')}
            </DialogDescription>
          ) : (
            <DialogDescription>{suggestDescription}</DialogDescription>
          )}
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                {t('freelancerOnboarding.portageContact.firstName')}{' '}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFirstName(e.target.value)
                  if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: '' }))
                }}
                className={errors.firstName ? 'border-red-500' : ''}
                placeholder={t('freelancerOnboarding.portageContact.firstNamePlaceholder')}
                autoComplete="given-name"
              />
              {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label>
                {t('freelancerOnboarding.portageContact.lastName')}{' '}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                value={lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setLastName(e.target.value)
                  if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: '' }))
                }}
                className={errors.lastName ? 'border-red-500' : ''}
                placeholder={t('freelancerOnboarding.portageContact.lastNamePlaceholder')}
                autoComplete="family-name"
              />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              {t('freelancerOnboarding.portageContact.email')} <span className="text-red-500">*</span>
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value)
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }))
              }}
              className={errors.email ? 'border-red-500' : ''}
              placeholder={t('freelancerOnboarding.portageContact.emailPlaceholder')}
              onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSubmit()}
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            {!isTeamVariant && (
              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <p className="text-sm text-gray-700">
                  {t('freelancerOnboarding.portageContact.suggestEmailHint')}
                </p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !portageCompanyId}>
            {isSubmitting
              ? t('common.loading')
              : isTeamVariant
                ? t('freelancerOnboarding.portageContact.addContact')
                : t('freelancerOnboarding.portageContact.suggestSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
